import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import AIAgentNetwork from "./AIAgentNetwork";
import { IMAGES } from "@/data/content";

export const Hero = ({ onStartProject }) => {
    const reduced = useReducedMotion();
    const hostRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: hostRef,
        offset: ["start start", "end start"],
    });
    const yBg = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 80]);
    const yMid = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 130]);
    const yFg = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 60]);
    const opTitle = useTransform(scrollYProgress, [0, 0.6], [1, 0.6]);

    return (
        <section
            ref={hostRef}
            className="relative pt-32 lg:pt-36 pb-20 lg:pb-28 overflow-hidden"
            aria-labelledby="hero-heading"
        >
            {/* Background layers */}
            <motion.div
                style={{ y: yBg }}
                className="absolute inset-0 hero-radial pointer-events-none"
            />
            <div className="absolute inset-0 dot-grid-fine opacity-70 pointer-events-none [mask-image:radial-gradient(ellipse_at_center,#000_30%,transparent_75%)]" />
            <div className="absolute inset-0 noise pointer-events-none" />

            <div className="relative mx-auto max-w-7xl container-px grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                <motion.div className="lg:col-span-7" style={{ opacity: opTitle }}>
                    <motion.span
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="inline-flex items-center gap-2 rounded-full ring-1 ring-black/10 bg-white/70 backdrop-blur px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))] shadow-[0_0_10px_rgba(187,118,33,0.55)]" />
                        AI Speed. Human Judgement.
                    </motion.span>
                    <motion.h1
                        id="hero-heading"
                        initial={{ opacity: 0, y: 16, filter: "blur(10px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-6 font-display text-[52px] sm:text-[80px] lg:text-[112px] leading-[0.96] tracking-[-0.03em] text-[hsl(var(--text))] text-balance"
                    >
                        Innovate{" "}
                        <span className="text-[hsl(var(--text))]/85">with</span>{" "}
                        <span className="italic text-[hsl(var(--gold))]">IdeaClap</span>
                        <span className="text-[hsl(var(--text))]/85">.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-7 max-w-xl text-[15.5px] sm:text-[17.5px] leading-[1.65] text-[hsl(var(--text-muted))] text-pretty"
                    >
                        IdeaClap combines creative and marketing expertise alongside AI agents
                        and expert human talent to build growth systems and apps that help
                        brands launch, scale, and perform.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.25 }}
                        className="mt-9 flex flex-wrap items-center gap-3"
                    >
                        <button
                            type="button"
                            onClick={onStartProject}
                            data-testid="hero-start-project-button"
                            className="sheen group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/90 text-[hsl(var(--bg))] px-5 py-3 text-[14.5px] font-semibold transition-colors"
                        >
                            Start a Project
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </button>
                        <a
                            href="#capabilities"
                            data-testid="hero-explore-capabilities-button"
                            className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-white/90 ring-1 ring-inset ring-black/10 text-[hsl(var(--text))] px-5 py-3 text-[14.5px] font-semibold transition-colors"
                        >
                            Explore Capabilities
                        </a>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                        className="mt-8 flex flex-wrap items-center gap-4 text-[12.5px] text-[hsl(var(--text-subtle))] font-mono uppercase tracking-[0.18em]"
                    >
                        <span className="inline-flex items-center gap-2">
                            <span className="h-1 w-1 rounded-full bg-[hsl(var(--blue))]" />
                            Curated specialists
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <span className="h-1 w-1 rounded-full bg-[hsl(var(--gold))]" />
                            Human judgment
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <span className="h-1 w-1 rounded-full bg-[hsl(var(--blue))]" />
                            Systemized growth
                        </span>
                    </motion.div>
                </motion.div>

                {/* Right cinematic visual stack */}
                <div className="lg:col-span-5">
                    <div className="relative aspect-[4/4.6] sm:aspect-[5/5] lg:aspect-[5/5.6] w-full">
                        <motion.div
                            style={{ y: yMid }}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute inset-0 rounded-3xl border border-black/10 bg-white overflow-hidden shadow-deep"
                        >
                            <img
                                src={IMAGES.hero}
                                alt="Editorial creative intelligence visual"
                                className="absolute inset-0 h-full w-full object-cover"
                                loading="eager"
                            />
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(247,242,232,0.0) 30%, rgba(247,242,232,0.55) 100%)",
                                }}
                            />
                            <AIAgentNetwork
                                className="absolute inset-0 opacity-90 mix-blend-multiply"
                                reactive
                            />
                        </motion.div>

                        {/* Floating mockup cards */}
                        <motion.div
                            style={{ y: yFg }}
                            initial={{ opacity: 0, y: 20, rotate: -3 }}
                            animate={{ opacity: 1, y: 0, rotate: -2 }}
                            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                            className={`absolute top-6 left-6 sm:top-8 sm:left-8 w-[58%] max-w-[300px] rounded-2xl glass-strong shadow-deep p-3.5 ${reduced ? "" : "float-y"}`}
                        >
                            <div className="flex items-center justify-between text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                <span>Project Brief</span>
                                <span className="text-[hsl(var(--gold))]">in motion</span>
                            </div>
                            <div className="mt-2.5 font-display text-[15.5px] leading-snug text-[hsl(var(--text))]">
                                Your next growth engine, mapped.
                            </div>
                            <div className="mt-2 grid grid-cols-3 gap-1.5">
                                <div className="h-1.5 rounded-full bg-[rgba(37,99,235,0.55)]" />
                                <div className="h-1.5 rounded-full bg-[rgba(37,99,235,0.30)]" />
                                <div className="h-1.5 rounded-full bg-[rgba(187,118,33,0.55)]" />
                            </div>
                            <div className="mt-2 text-[11px] text-[hsl(var(--text-subtle))]">
                                Discovery → Strategy → Build
                            </div>
                        </motion.div>

                        <motion.div
                            style={{ y: yFg }}
                            initial={{ opacity: 0, y: 20, rotate: 4 }}
                            animate={{ opacity: 1, y: 0, rotate: 3 }}
                            transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className={`absolute bottom-8 right-4 sm:bottom-10 sm:right-6 w-[64%] max-w-[330px] rounded-2xl glass-strong shadow-deep p-3.5 ${reduced ? "" : "float-y-slow"}`}
                        >
                            <div className="flex items-center justify-between text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                <span>Pipeline lift</span>
                                <span className="text-[hsl(var(--text))]">last 30d</span>
                            </div>
                            <div className="mt-2 font-display text-3xl text-[hsl(var(--text))] tracking-[-0.02em] leading-none num-tabular">
                                +4.6<span className="text-[hsl(var(--gold))]">x</span>
                            </div>
                            <div className="mt-3 flex items-end gap-1.5 h-10">
                                {[24, 36, 30, 48, 42, 60, 56, 72, 80, 76, 90, 100].map((h, i) => (
                                    <span
                                        key={i}
                                        style={{ height: `${h}%` }}
                                        className={`w-2 rounded-sm ${
                                            i % 4 === 3
                                                ? "bg-[hsl(var(--gold))]"
                                                : "bg-[rgba(37,99,235,0.65)]"
                                        }`}
                                    />
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 16, rotate: 1 }}
                            animate={{ opacity: 1, y: 0, rotate: 0 }}
                            transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute top-1/2 right-2 sm:right-4 -translate-y-1/2 w-[46%] max-w-[230px] rounded-2xl glass-strong shadow-glow-blue p-3.5"
                        >
                            <div className="flex items-center gap-2 text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--blue))]" />
                                Agent workflow
                            </div>
                            <div className="mt-2 space-y-1.5">
                                {["research_agent", "draft_agent", "qa_agent"].map((a) => (
                                    <div
                                        key={a}
                                        className="flex items-center justify-between text-[12px] font-mono text-[hsl(var(--text))]"
                                    >
                                        <span>{a}</span>
                                        <span className="text-[hsl(var(--gold))]">ok</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
